import type { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core/styles';

/*
 * Skin for the catalog Links card.
 *
 * The vendor lays its links out as a BUI `Grid` of `IconLink`s — one to three
 * columns depending on width, each a flex box of an icon and a `Link` that
 * wraps to as many lines as it needs. In the rail that renders as a label
 * folded under its own icon.
 *
 * The mock's Links row, measured from its own inline styles:
 *
 *   display:flex; align-items:center; gap:10px; height:38px; padding:0 20px;
 *   border-bottom:1px solid #f7f7f6
 *     · 13px leading icon at opacity 0.55
 *     · label, 12.5px/500, link blue, on ONE line
 *     · spacer
 *     · 10px external-link glyph at opacity 0.4
 *
 * Link colour is deliberately absent here — it comes from the global link
 * treatment, which already resolves to the mock's #039cfb.
 */

/* The mock's row separator. Lighter than the header rule (#f0f0ef); no token. */
const ROW_HAIRLINE = 'var(--hone-portal-hairline-row)';

const useStyles = makeStyles({
  root: {
    display: 'contents',

    /*
     * Rows run to the card's edge, so the body padding moves onto them. Guarded
     * on the grid being present: with no links the card renders an empty state
     * (prose, a code sample and a button) that still wants its normal padding.
     */
    '& [class*="bui-CardBody"]:has([class*="bui-Grid"])': {
      padding: 0,
    },
    '& [class*="bui-Grid"]': {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    },

    '& [class*="bui-Grid"] > [class*="MuiBox-root"]': {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minHeight: 38,
      padding: '0 20px',
      borderBottom: `1px solid ${ROW_HAIRLINE}`,
    },

    /* Leading icon. The vendor spaces it with `mr: 1`; the row gap replaces it. */
    '& [class*="bui-Grid"] > [class*="MuiBox-root"] > [class*="MuiBox-root"]:first-child':
      {
        marginRight: 0,
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        fontSize: 13,
        color: 'var(--bui-fg-secondary)',
      },
    '& [class*="bui-Grid"] > [class*="MuiBox-root"] [class*="MuiSvgIcon-root"]':
      {
        fontSize: 13,
      },

    /* One line, ellipsised — the wrap under the icon is the reported defect. */
    '& [class*="bui-Grid"] > [class*="MuiBox-root"] > [class*="MuiBox-root"]:last-child':
      {
        flex: 1,
        minWidth: 0,
      },
    '& [class*="bui-Grid"] > [class*="MuiBox-root"] a': {
      display: 'block',
      fontSize: 12.5,
      fontWeight: 500,
      lineHeight: 1.4,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    /*
     * Trailing external-link glyph. The vendor renders none, and a pseudo
     * element is the only way to add one from a skin — every link on this card
     * opens in a new tab, so it applies to all of them.
     */
    '& [class*="bui-Grid"] > [class*="MuiBox-root"]::after': {
      content: '"\\2197"',
      flex: '0 0 auto',
      fontSize: 11,
      lineHeight: 1,
      color: 'var(--hone-portal-fg-muted)',
    },
  },
});

export function LinkRows({ children }: { children: ReactNode }) {
  const classes = useStyles();
  return <div className={classes.root}>{children}</div>;
}
